import { isEmpty } from 'lodash/fp';
import { flow, pipe } from 'fp-ts/function';
import { array, ord, string } from 'fp-ts';
import { TextKey } from '@mews-commander/enums';
import { IdentityDocument } from '@mews-commander/data-access';
import { createAsyncActionTypes } from '@mews/framework';
import { ApplicationState, WithOwnerId } from '@mews-commander/types';
import { IdentityDocumentType } from '@mews-commander/legacy-data-structures';
import { createFetchAction, localizeEnumify, localizeText } from '@mews-commander/utils';
import prefix from '../prefix';
import parentSelector from '../selector';
import { createEntitiesReducer, createBaseEntitySelectors } from './utils';

export const NAME = 'identityDocuments';

export const IDENTITY_DOCUMENTS_LOADED = prefix('IDENTITY_DOCUMENTS_LOADED');

export const IDENTITY_DOCUMENT_ADD = prefix('IDENTITY_DOCUMENT_ADD');
export const [IDENTITY_DOCUMENT_ADD_STARTED, IDENTITY_DOCUMENT_ADD_SUCCEEDED, IDENTITY_DOCUMENT_ADD_FAILED] =
    createAsyncActionTypes(IDENTITY_DOCUMENT_ADD);
export const IDENTITY_DOCUMENT_UPDATE = prefix('IDENTITY_DOCUMENT_UPDATE');
export const [IDENTITY_DOCUMENT_UPDATE_STARTED, IDENTITY_DOCUMENT_UPDATE_SUCCEEDED, IDENTITY_DOCUMENT_UPDATE_FAILED] =
    createAsyncActionTypes(IDENTITY_DOCUMENT_UPDATE);
export const IDENTITY_DOCUMENT_DELETE = prefix('IDENTITY_DOCUMENT_DELETE');
export const [IDENTITY_DOCUMENT_DELETE_STARTED, IDENTITY_DOCUMENT_DELETE_SUCCEEDED, IDENTITY_DOCUMENT_DELETE_FAILED] =
    createAsyncActionTypes(IDENTITY_DOCUMENT_DELETE);

export const setIdentityDocuments = (identityDocuments: Array<IdentityDocument>, ownerId: string) =>
    createFetchAction(IDENTITY_DOCUMENTS_LOADED, { entities: identityDocuments, ownerId });

export default createEntitiesReducer({
    FETCH_SUCCESS: IDENTITY_DOCUMENTS_LOADED,
    ADD_SUCCESS: IDENTITY_DOCUMENT_ADD_SUCCEEDED,
    UPDATE_SUCCESS: IDENTITY_DOCUMENT_UPDATE_SUCCEEDED,
    DELETE_SUCCESS: IDENTITY_DOCUMENT_DELETE_SUCCEEDED,
});

export const [getIdentityDocuments, getIdentityDocumentsByIds, getIdentityDocumentsByOwnerId, getIdentityDocument] =
    createBaseEntitySelectors<ApplicationState, IdentityDocument & WithOwnerId, typeof NAME>(parentSelector, NAME);

export const getIdentityDocumentName = (identityDocument: IdentityDocument) => {
    if (!identityDocument) {
        return '';
    }

    const documentType = identityDocument.type && IdentityDocumentType.getByName(identityDocument.type);
    const type = documentType ? `${localizeEnumify(documentType)} ` : '';
    return isEmpty(identityDocument.number) ? `${type}` : `${type}${identityDocument.number}`;
};

export const getIdentityDocumentScans = (identityDocument: IdentityDocument) => {
    if (identityDocument == null) {
        return [];
    }
    return identityDocument.scans ?? [];
};

export const getIdentityDocumentTitle = (identityDocument: IdentityDocument) => {
    const documentName = getIdentityDocumentName(identityDocument);

    return !isEmpty(documentName) ? documentName : localizeText(TextKey.IdentityDocument);
};

const byUpdatedUtc = pipe(
    ord.reverse(string.Ord),
    ord.contramap(({ createdUtc }: IdentityDocument) => createdUtc)
);

export const getSortedIdentityDocumentsByOwnerId = flow(
    getIdentityDocumentsByOwnerId,
    array.sortBy([byUpdatedUtc]),
    array.map(({ id }) => id)
);
